import {driverSession,markDriverLoaded,driverAttempt,deferDriverAttempt,finishDriverAttempt,
  driverRequest,validateDriverResponse,demoDelivery} from './driver-api.js';

const React = window.React;
const {useEffect,useRef,useState} = React;
// STARTWERTE: sämtliche Fahrertexte, feste Demodaten, 15 s Küchenabfrage,
// 2 s Wiederholung ohne Serverfrist. Retry-After hat Vorrang.
const labels = {ACCEPTED:'Bestellung angenommen',PREPARING:'In Zubereitung',READY:'Bereit zur Lieferung',
  OUT_FOR_DELIVERY:'Unterwegs',HANDED_OVER:'Zugestellt – danke'};
const waitingText = 'Küche hat noch nicht fertig gemeldet – Seite aktualisiert sich';
const storageText = 'Browserspeicher nicht verfügbar. Bitte den Fahrer-Link erneut öffnen.';
const nextState = state => ['PREPARING','READY'].includes(state) ? 'OUT_FOR_DELIVERY' : state === 'OUT_FOR_DELIVERY' ? 'HANDED_OVER' : null;
const retryable = error => !error.status || error.status === 429 || error.status >= 500;
const retryAt = error => Math.max(Date.now(),error.retryAt || Date.now()+2000);

export default function DriverDelivery({components,demo}) {
  const {StatusScreen,STR} = components;
  const [data,setData] = useState(null), [notice,setNotice] = useState('Lieferung wird geladen …');
  const [busy,setBusy] = useState(true), [failed,setFailed] = useState(false);
  const action = useRef(null);

  useEffect(() => {
    let stopped = false, timer, session, current = null, locked = true;
    const controller = new AbortController();
    // Ein weiterer Fahrer-Link im selben Tab muss den Zugang erneut übernehmen.
    const newLink = () => {
      if (!location.hash) return;
      stopped=true;locked=true;clearTimeout(timer);controller.abort();location.reload();
    };
    window.addEventListener('hashchange',newLink);
    const update = value => { current=value;setData(value); };
    const unlock = () => { locked=false;setBusy(false); };
    const storageFailure = () => { locked=true;setBusy(true);setFailed(true);setNotice(storageText); };
    const later = (run,at) => {
      clearTimeout(timer);
      // Lange HTTP-Datumsfristen ohne Überlauf des Browser-Timers respektieren.
      timer=setTimeout(() => {
        if (stopped) return;
        if (Date.now()<at) later(run,at); else run();
      },Math.min(60000,Math.max(0,at-Date.now())));
    };
    const unavailable = () => {
      setNotice(session?.loaded ? 'Lieferung abgeschlossen oder Link ungültig' : 'Link ungültig oder Lieferung abgeschlossen');
      update(null);setFailed(true);locked=true;setBusy(false);
    };
    async function load(message='') {
      locked=true;setBusy(true);
      try {
        const result=validateDriverResponse(await driverRequest(session,controller.signal),session.orderId);
        if (stopped) return;
        try { markDriverLoaded(session); } catch { storageFailure();return; }
        update(result);setFailed(false);setNotice(message);unlock();
        if ([null,'ACCEPTED','PREPARING'].includes(result.kitchenState)) later(()=>load(message),Date.now()+15000);
      } catch (error) {
        if (stopped) return;
        if (error.status===404) { unavailable();return; }
        if (retryable(error)) {
          setNotice('Bitte kurz warten');later(()=>load(message),retryAt(error));
        } else { setFailed(true);setNotice('Lieferung konnte nicht geladen werden. Bitte den Link erneut öffnen.'); }
      }
    }
    async function send(state,attempt) {
      if (stopped) return;
      if (attempt.retryAt>Date.now()) { later(()=>send(state,attempt),attempt.retryAt);return; }
      try {
        const result=validateDriverResponse(await driverRequest(session,controller.signal,state,attempt),session.orderId,state);
        if (stopped) return;
        try { finishDriverAttempt(session.orderId,state); } catch { storageFailure();return; }
        update({...current,...result});setNotice('');unlock();
        // Nach Übergabe ausschließlich die letzte Antwort zeigen: kein Folge-GET.
      } catch (error) {
        if (stopped) return;
        if (retryable(error)) {
          try { deferDriverAttempt(session.orderId,state,attempt,retryAt(error)); }
          catch { storageFailure();return; }
          setNotice('Bitte kurz warten');later(()=>send(state,attempt),attempt.retryAt);return;
        }
        if (error.status>=400 && error.status<500) {
          try { finishDriverAttempt(session.orderId,state); } catch { storageFailure();return; }
        }
        if (error.status===404) { unavailable();return; }
        if (error.status===409) {
          // Erweiterungsdaten allein geben keinen Knopf frei; autoritativen Stand neu lesen.
          await load('Der Lieferstatus hat sich geändert. Der aktuelle Stand wurde neu geladen.');return;
        }
        setFailed(true);setNotice('Status konnte nicht geändert werden. Bitte den Fahrer-Link erneut öffnen.');
      }
    }
    action.current = () => {
      if (locked || stopped) return;
      const state=nextState(current?.kitchenState);
      if (!state) return;
      locked=true;
      if (state==='HANDED_OVER' && !window.confirm('Lieferung als zugestellt bestätigen?')) { locked=false;return; }
      clearTimeout(timer);setBusy(true);setNotice('');
      if (demo) { update({...current,kitchenState:state});unlock();return; }
      let attempt;
      try { attempt=driverAttempt(session.orderId,state); }
      catch { storageFailure();return; }
      if (attempt.retryAt>Date.now()) setNotice('Bitte kurz warten');
      void send(state,attempt);
    };
    try { session=driverSession(demo); }
    catch { storageFailure(); }
    if (demo) { update(demoDelivery);setNotice('');unlock(); }
    else if (session) void load();
    else if (!session && !location.hash) {
      // Speicherfehler behalten ihren eigenen Hinweis; fehlende Zugangsdaten führen zu keinem Request.
      setNotice(value=>value===storageText ? value : 'Link ungültig oder Lieferung abgeschlossen');setFailed(true);setBusy(false);
    }
    return () => { stopped=true;clearTimeout(timer);controller.abort();window.removeEventListener('hashchange',newLink);action.current=null; };
  },[demo]);

  const state=data?.kitchenState, stateText=labels[state] || 'Lieferstatus noch nicht verfügbar';
  const address=data?.address, addressText=address ? [address.street,[address.postalCode,address.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') : '';
  const phone=data?.customer?.phone, dial=typeof phone==='string' ? phone.replace(/[^+0-9*#,;]/g,'') : '';
  const requested=data?.requestedFor ? new Date(data.requestedFor) : null;
  const requestedText=requested && !Number.isNaN(requested.getTime()) ? requested.toLocaleString('de-DE',{dateStyle:'short',timeStyle:'short'}) : null;
  const number=data ? String(data.orderNo).replace(/^O-/,'') : null;
  const details=<>
    {notice && <p className="minnote" role="alert">{notice}</p>}
    {data && <>
      {state==='ACCEPTED' && <p className="minnote">{waitingText}</p>}
      <div className="field"><label>Lieferadresse</label>
        {addressText ? <a href={'https://www.google.com/maps/search/?api=1&query='+encodeURIComponent(addressText)} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">{addressText}</a> : <p>Keine Lieferadresse hinterlegt</p>}
        {address?.note && <p>{address.note}</p>}
      </div>
      <div className="field"><label>Kontakt</label><p>{[data.customer?.firstName,data.customer?.lastName].filter(Boolean).join(' ')}</p>
        {phone && dial && <a href={'tel:'+dial}>{phone}</a>}
      </div>
      <div className="field"><label>Positionen</label><ul>{data.items.map((item,index)=><li key={index}>{String(item.quantity)} × {item.name}{item.variant ? ' · '+item.variant : ''}</li>)}</ul></div>
      {nextState(state) && <button className="cartbtn block" disabled={busy || failed} onClick={()=>action.current?.()}>{state==='OUT_FOR_DELIVERY'?'Zugestellt':'Unterwegs'}</button>}
      {state==='PREPARING' && <p className="minnote">Mit „Unterwegs“ meldest du die Bestellung auch als fertig.</p>}
    </>}
  </>;
  return <StatusScreen t={{...STR.de,trackHead:'Lieferstatus',trackSub:data?stateText:'Auf dieser Seite kannst du den Lieferstatus prüfen.'}}
    mode="delivery" orderNo={number===null?null:'O-'+number} step={['READY','OUT_FOR_DELIVERY','HANDED_OVER'].indexOf(state)}
    online={{title:number===null?'Lieferung':'Lieferung O-'+number,hideLanguage:true,failed,canNew:false,
      etaLabel:requestedText?'Wunschzeit':'Status',eta:requestedText || (data?stateText:'—'),
      steps:data?[['Bereit zur Lieferung','','PackageCheck'],['Unterwegs','','Bike'],['Zugestellt','','CircleCheck']]:[],
      notice:details,footer:demo?'Demo · unverbindlich':''}}/>;
}
